import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sparkle, ScanText, BookOpen, Gamepad2, Orbit, Wand2, Library,
  CreditCard, User, Shield, LogOut, Menu, Compass, BookMarked, Trophy,
} from "lucide-react";
import { BrandLockup } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const MOBILE_TABS = [
  { to: "/today", label: "Today", icon: Sparkle, testid: "nav-today" },
  { to: "/path", label: "Path", icon: Gamepad2, testid: "nav-path" },
  { to: "/decode", label: "Decode", icon: ScanText, testid: "nav-decode" },
  { to: "/doctrine", label: "Doctrine", icon: BookOpen, testid: "nav-doctrine" },
  { to: "/vault", label: "Vault", icon: Library, testid: "nav-vault" },
];

const RAIL = [
  { to: "/today", label: "Today", icon: Sparkle, testid: "nav-today" },
  { to: "/path", label: "Path", icon: Gamepad2, testid: "nav-path" },
  { to: "/doctrine", label: "Doctrine", icon: BookOpen, testid: "nav-doctrine" },
  { to: "/decode", label: "Decode", icon: ScanText, testid: "nav-decode" },
  { to: "/universe", label: "Universe", icon: Orbit, testid: "nav-universe" },
  { to: "/studio", label: "Studio", icon: Wand2, testid: "nav-studio" },
  { to: "/vault", label: "Vault", icon: Library, testid: "nav-vault" },
  { to: "/gallery", label: "Gallery", icon: Compass, testid: "nav-gallery" },
  { to: "/dictionary", label: "Dictionary", icon: BookMarked, testid: "nav-dictionary" },
  { to: "/challenges", label: "Challenges", icon: Trophy, testid: "nav-challenges" },
];

const MORE = [
  { to: "/path", label: "Path", icon: Gamepad2 },
  { to: "/universe", label: "Universe", icon: Orbit },
  { to: "/studio", label: "Studio", icon: Wand2 },
  { to: "/gallery", label: "Gallery", icon: Compass },
  { to: "/dictionary", label: "Dictionary", icon: BookMarked },
  { to: "/challenges", label: "Challenges", icon: Trophy },
  { to: "/pricing", label: "Pricing", icon: CreditCard },
  { to: "/profile", label: "Profile", icon: User },
];

export const AppShell = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const active = (to) => location.pathname === to;
  const isStaff = ["moderator", "admin", "owner"].includes(user?.role) || user?.is_admin;
  const doLogout = async () => { await logout(); navigate("/"); };
  const go = (to) => { setMoreOpen(false); navigate(to); };

  return (
    <div className="min-h-screen flex bg-[#0B0C0E]">
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-white/[0.06] p-4 sticky top-0 h-screen overflow-auto scrollbar-thin">
        <button onClick={() => navigate("/today")} className="mb-7 text-left" data-testid="sidebar-brand"><BrandLockup /></button>
        <nav className="flex-1 space-y-1">
          {RAIL.map((n) => (
            <button key={n.to} onClick={() => navigate(n.to)} data-testid={n.testid}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${active(n.to) ? "bg-[rgba(20,90,67,0.22)] text-[var(--bone)] border border-[rgba(20,90,67,0.5)]" : "text-[var(--bone-muted)] hover:bg-white/[0.04]"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="space-y-1 pt-3 border-t border-white/[0.06]">
          <button onClick={() => navigate("/pricing")} data-testid="nav-pricing" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[var(--bone-muted)] hover:bg-white/[0.04]"><CreditCard className="w-4 h-4" /> Pricing</button>
          <button onClick={() => navigate("/profile")} data-testid="nav-profile" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[var(--bone-muted)] hover:bg-white/[0.04]"><User className="w-4 h-4" /> Profile</button>
          {isStaff && (<button onClick={() => navigate("/admin")} data-testid="nav-admin" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm gold-text hover:bg-white/[0.04]"><Shield className="w-4 h-4" /> Admin</button>)}
          <button onClick={doLogout} data-testid="nav-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[var(--bone-muted)] hover:bg-white/[0.04]"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 glass border-b border-white/[0.06] px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="md:hidden"><BrandLockup size={24} textClass="text-base" /></div>
          <div className="hidden md:block text-sm text-[var(--bone-faint)]">{[...RAIL, ...MORE].find((n) => active(n.to))?.label || "Litrametry"}</div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-[rgba(20,90,67,0.5)] green-text" data-testid="topbar-rank">{user?.rank || "Reader"}</Badge>
            <Badge className="gold-edge gold-text bg-transparent" data-testid="topbar-credits">{user?.credits ?? 0} cr</Badge>
            <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden p-1.5 rounded-lg hover:bg-white/[0.06]" data-testid="mobile-more-button"><Menu className="w-5 h-5" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0F1115] border-white/[0.1] w-72">
                <div className="mt-6 space-y-1">
                  {MORE.map((n) => (
                    <button key={n.to} onClick={() => go(n.to)} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-[var(--bone-muted)] hover:bg-white/[0.04]" data-testid={`more-${n.label.toLowerCase()}`}>
                      <n.icon className="w-4 h-4" /> {n.label}
                    </button>
                  ))}
                  {isStaff && <button onClick={() => go("/admin")} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm gold-text hover:bg-white/[0.04]"><Shield className="w-4 h-4" /> Admin</button>}
                  <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-[var(--bone-muted)] hover:bg-white/[0.04]"><LogOut className="w-4 h-4" /> Log out</button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-28 md:pb-10 max-w-6xl w-full mx-auto">{children}</main>
      </div>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-white/[0.08] flex justify-around px-1 py-2" data-testid="app-shell-bottom-nav">
        {MOBILE_TABS.map((n) => (
          <button key={n.to} onClick={() => navigate(n.to)} data-testid={`${n.testid}-mobile`}
            className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-[10px] ${active(n.to) ? "green-text" : "text-[var(--bone-faint)]"}`}>
            <n.icon className="w-5 h-5" /> {n.label}
          </button>
        ))}
      </nav>
    </div>
  );
};

export default AppShell;
